import type { FC } from "react";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type FlightFareViewProps,
} from "@b2bportal/core-types";
import { isMixedCabin } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  getSeatsRemaining,
  shouldShowSeatsRemaining,
  useGetFixedPricingFromFare,
} from "@overrides/utilities";
import { FareDetails } from "@tenant/flights";
import { Icon, Tooltip, usePriceDisplay } from "@tenant/ui";
import defaultStyles from "./FlightFareView.module.scss";
import { useIsPackageIncrementalPricing } from "@/pages/Packages/hooks";

const COMPONENT_KEY = CoreFlightsComponent.FlightFareView;

/**
 * A Flex-only fare (below the Grid minimum) replaces price + Continue with
 * "Available with Flex" and a "View Flex pricing" button, so the handler is
 * required with the flag: a Flex-only fare with no way out is a dead end.
 */
type FlexOnlyProps =
  | {
      isFlexOnly: true;
      /** The client asks to see this fare's Flex pricing. */
      onViewFlexPricing: () => void;
    }
  | { isFlexOnly?: false; onViewFlexPricing?: undefined };

export const FlightFareView: FC<FlightFareViewProps & FlexOnlyProps> = ({
  fareDetails,
  tripDetails,
  sliceIndex,
  onSelect,
  isFlexOnly = false,
  onViewFlexPricing,
}) => {
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();
  const getFixedPricingFromFare = useGetFixedPricingFromFare();
  // A Flex-only fare has no fixed-points pricing to derive.
  const prices = isFlexOnly
    ? undefined
    : getFixedPricingFromFare(fareDetails.paxPricings?.[0]);

  const isIncrementalPricing = useIsPackageIncrementalPricing();

  const { rewardsThenPriceText } = usePriceDisplay({
    prices,
    inline: "priceOnly",
    formatOptions: isIncrementalPricing ? { signDisplay: "always" } : undefined,
  });

  const seatsRemaining = getSeatsRemaining(fareDetails.slices[sliceIndex]);

  const showMixedCabin = isMixedCabin(fareDetails.slices[sliceIndex]);

  return (
    <>
      <div className={cn("fare-title")}>
        {fareDetails.slices[sliceIndex].fareShelf?.brandName ?? ""}
      </div>
      <div className={cn("fare-details")}>
        <FareDetails
          segments={tripDetails.slices[sliceIndex].segmentDetails}
          fareSlice={fareDetails.slices[sliceIndex]}
          restrictions={fareDetails.restrictions?.[sliceIndex]}
          // Update if we want to show more fare details at once
          numberOfFareDetails={6}
        />
      </div>
      {showMixedCabin && (
        <div className={cn("mixed-cabin")}>
          <Tooltip
            content={
              <p className={cn("tooltip-text")}>{t("mixedCabin.tooltip")}</p>
            }
            customIcon={
              <Icon
                className={cn("tooltip-icon")}
                iconName={IconNameEnum.info}
              />
            }
          >
            <p>{t("mixedCabin.title")}</p>
          </Tooltip>
        </div>
      )}
      <div className={cn("fare-select")}>
        {prices !== null && (
          <>
            {shouldShowSeatsRemaining(seatsRemaining) && (
              <p className={cn("seats-remaining")}>
                {t("seatsRemaining", { count: seatsRemaining })}
              </p>
            )}
            {isFlexOnly ? (
              <>
                <p className={cn("continue-price")}>
                  {t("gridV2.flexOnlyFare.availableWithFlex")}
                </p>
                <button
                  type="button"
                  className={cn("fare-button")}
                  onClick={onViewFlexPricing}
                >
                  {t("gridV2.flexOnlyFare.viewFlexPricing")}
                </button>
              </>
            ) : (
              <>
                <p className={cn("continue-price")}>{rewardsThenPriceText}</p>
                <button className={cn("fare-button")} onClick={onSelect}>
                  {t("continue")}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};
